import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { MdOutlineKeyboardDoubleArrowDown } from 'react-icons/md';
import { RxArrowTopRight } from 'react-icons/rx';
import ScholarshipCardBg from '../../../public/images/scholarship-card-bg.svg';
import ScholarshipCardBgMobile from '../../../public/images/scholarship-card-mobile.svg';
import { useWindowSize } from '../../utils/useWindowSize';
import { trackApplyButton } from '@/utils/dataLayer';

const BASE_PRICE = 450;

const ScholarshipCard = ({ programType }) => {
    const { t, i18n } = useTranslation();
    const { width } = useWindowSize();
    const [percentage, setPercentage] = useState(50);

    const price = Math.round(BASE_PRICE * (1 - percentage / 100));
    const savings = BASE_PRICE - price;

    const benefits = [
        t('scholarship-card.benefit-1'),
        t('scholarship-card.benefit-2'),
        t('scholarship-card.benefit-3'),
        t('scholarship-card.benefit-4'),
        t('scholarship-card.benefit-5'),
    ];

    const isMobile = width !== undefined && width < 640;
    const isEnglish = i18n?.language?.startsWith('en');

    return (
        <div className="w-full max-w-[90%] mx-auto py-[80px] md:py-[100px]">
            <div className="flex flex-col-reverse lg:flex-row gap-[40px] md:gap-[60px] xl:gap-[80px] items-center">
                <div className="flex-1 w-full flex flex-col gap-[16px]">
                    <h2 className="text-[32px] md:text-[48px] lg:text-[56px] xl:text-[60px] font-[500] leading-tight">
                        <span className="text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884]" dangerouslySetInnerHTML={{ __html: t('scholarship-card.title-part-1') }} />
                    </h2>

                    <div className="flex flex-col gap-[16px] md:gap-[20px]">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="flex items-center gap-[12px] md:gap-[16px]">
                                <div className="min-w-[24px] min-h-[24px] md:min-w-[32px] md:min-h-[32px] rounded-full flex items-center justify-center relative flex-shrink-0">
                                    <span className="absolute inset-0 rounded-full bg-gradient-to-b from-[#091F59] to-[#1A3884]"></span>
                                    <MdOutlineKeyboardDoubleArrowDown className="w-[16px] h-[16px] md:w-[18px] md:h-[18px] relative z-10 text-white" />
                                </div>
                                <p className="text-[16px] font-[400] text-[#091F59] leading-relaxed">
                                    {benefit}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                <div
                    className="flex-1 relative rounded-[24px] md:rounded-[32px] overflow-hidden min-w-[310px] max-w-[310px] min-h-[280px] max-h-[280px] xs:min-h-[303px] xs:max-h-[303px] xs:min-w-[343px] xs:max-w-[343px] sm:min-h-[357px] sm:max-h-[357px] sm:min-w-[500px] sm:max-w-[500px]"
                    style={{
                        backgroundImage: `url(${isMobile ? ScholarshipCardBgMobile.src : ScholarshipCardBg.src})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat'
                    }}>
                    <div className="relative h-full flex flex-col gap-[16px] sm:gap-[20px] p-[20px] sm:px-[40px] sm:py-[30px]">
                        <div className="absolute top-2 right-3 xs:top-3 xs:right-5 z-10 w-[100px] sm:w-[160px] h-[40px] rounded-full">
                            <span className="w-full h-full flex items-center justify-center text-white text-[12px] sm:text-[18px] font-[400] px-[16px] md:px-[20px] py-[8px] md:py-[10px] rounded-full inline-block bg-gradient-to-b from-[#091F59] to-[#1A3884]">
                                {t('scholarship-card.monthly')}
                            </span>
                        </div>

                        <div className="flex flex-col gap-[8px] relative z-10">
                            <h3 className={`text-main-pink font-[600] ${isEnglish ? 'text-[14px] sm:text-[22px]' : 'text-[18px] sm:text-[24px]'}`}>
                                {t('scholarship-card.card-title')}
                            </h3>
                            <div className="flex flex-col">
                                <span className="text-white text-[40px] sm:text-[56px] font-[700] leading-none">
                                    {price}<span className="text-[28px] sm:text-[40px]">₼</span>
                                </span>
                                <p className={`text-[#8792B0] font-[400] mt-[4px] ${isEnglish ? 'text-[12px] sm:text-[13px]' : 'text-[14px]'}`}>
                                    {t('scholarship-card.save-text', { savings })}
                                </p>
                            </div>
                        </div>
                        <div className="relative z-10 select-none">
                            <div className="relative h-[44px] sm:h-[50px]">
                                <div className="relative">
                                    <div
                                        className="absolute flex flex-col items-center"
                                        style={{ left: `${percentage}%`, transform: "translateX(-50%)" }}
                                    >
                                        <div className="relative w-[28px] h-[34px] sm:w-[35px] sm:h-[38px]">
                                            <svg
                                                viewBox="0 0 100 120"
                                                className="w-full h-full"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M50 0C22 0 0 22 0 50C0 85 50 120 50 120C50 120 100 85 100 50C100 22 78 0 50 0Z"
                                                    fill="#FF3366"
                                                />
                                                <text x="50" y="58" textAnchor="middle" fill="white" fontSize="28" fontWeight="700">
                                                    {percentage}%
                                                </text>
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div className="relative h-[24px] flex items-center">
                                <div className="absolute left-0 right-0 h-[6px] bg-[#1A2A5E] rounded-full">
                                    <div
                                        className="absolute top-0 left-0 h-full bg-[#FF3366] rounded-full"
                                        style={{ width: `${percentage}%` }}
                                    />
                                </div>
                                <div
                                    className="absolute w-[18px] h-[18px] sm:w-[22px] sm:h-[22px] bg-[#FF3366] rounded-full shadow-lg pointer-events-none"
                                    style={{ left: `${percentage}%`, transform: 'translateX(-50%)' }}
                                />
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={percentage}
                                    onChange={(e) => setPercentage(Number(e.target.value))}
                                    aria-label={t('scholarship-card.scholarship-slider-label')}
                                    aria-valuetext={`${percentage}%`}
                                    className="absolute left-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </div>
                        </div>

                        <Link href="/apply" className="relative z-10" onClick={() => trackApplyButton("scholarship_card")}>
                            <button className={`w-[250px] sm:w-full mx-auto sm:mx-0 h-[40px] xs:h-[48px] md:h-[56px] bg-white text-[#274DE0] rounded-full font-[500] flex items-center justify-center gap-[8px] hover:bg-white/90 transition-all duration-300 ${isEnglish ? 'text-[14px] sm:text-[16px] leading-tight' : ''}`}>
                                {programType === 'summer-bootcamp'
                                    ? t('scholarship-card.apply-button-summer-bootcamp')
                                    : t('scholarship-card.apply-button')}
                                <RxArrowTopRight className="w-[20px] h-[20px] md:w-[24px] md:h-[24px]" />
                            </button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScholarshipCard;
